<template>
  <div class="table-wrapper">
    <div class="table-caption">{{ $t('tenancy.table.emergencyContacts') }} <small>({{ $t('hint.provideLocalPhone') }})</small></div>
    <div
      class="link__contact"
      v-if="allowLinkContacts"
    >
      <button
        type="button"
        class="btn link__contact--btn"
        :disabled="!tenancyId"
        @click="linkFunc({isEmergency: true})"
      >{{ $t('tenancy.table.linkContact') }}</button>
      <span class="link__contact--plus">+</span>
    </div>
    <table>
      <thead>
        <tr>
          <th
            v-for="(header, index) in itemHeaders"
            :class="header.class"
            :key="index"
          >{{ header.name }}</th>
          <th class="column-header-remarks">{{ $t('tenancy.table.remarks') }}</th>
          <th class="column-header-remarks" />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, key) in emergencyContacts"
          :key="getKey(key)"
        >
          <td class="number-column">
            <div v-if="canAccessContact">
              <a
                class="name-hyper-link"
                @click="navToContactDetail(item.id, true)"
              >{{ displayI18nFullName(item.firstName, item.lastName) || item.name }}</a>
              <i
                class="remark-included-flag"
                v-if="isEngineer"
                v-show="!!item.remarks"
              />
            </div>
            <div v-else>
              {{ displayI18nFullName(item.firstName, item.lastName) || item.name }}
            </div>
          </td>
          <td>{{ getAllPhones(item) }}</td>
          <td
            class="check-column"
            :class="{'error-highlight-background': isItemRequireHighlight(item)}"
          >
            <input
              :id="'checkbox_' + key"
              type="checkbox"
              v-hkldinput
              :disabled="!allowLinkContacts"
              :checked="item.workingHourOrder ? 'checked' : ''"
              @change="updatePositionCheckBox(item.index, key, !item.workingHourOrder, true)"
            >
          </td>
          <td>
            <HKLSelect
              :disabled="!allowLinkContacts || !item.workingHourOrder"
              v-hkldinput
              :value="item.workingHourOrder || undefined"
              :options="workingHourPositionHklOptions"
              :showSearch="true"
              placeholder=""
              @change="val => updatePosition(key, 'workingHourOrder', val)"
            />
          </td>
          <td
            class="check-column"
            :class="{'error-highlight-background': isItemRequireHighlight(item)}"
          >
            <input
              :id="'checkbox_' + key"
              type="checkbox"
              v-hkldinput
              :disabled="!allowLinkContacts"
              :checked="item.nonWorkingHourOrder ? 'checked' : ''"
              @change="updatePositionCheckBox(item.index, key, !item.nonWorkingHourOrder, false)"
            >
          </td>
          <td>
            <HKLSelect
              :disabled="!allowLinkContacts || !item.nonWorkingHourOrder"
              v-hkldinput
              :value="item.nonWorkingHourOrder || undefined"
              :options="nonWorkingHourPositionHklOptions"
              :showSearch="true"
              placeholder=""
              @change="val => updatePosition(key, 'nonWorkingHourOrder', val)"
            />
          </td>
          <td>
            <TextField
              v-hkldinput
              parentNode=""
              :value="item.oRemarks"
              :disabled="!allowLinkContacts"
              :readonly="!allowLinkContacts"
              @updateValue="val => updateRemarks(item.index, val)"
            />
          </td>
          <td class="icon-remove">
            <i
              class="fa fa-times-thin"
              v-if="allowLinkContacts"
              @click="openDeleteConfirm(item.index, key)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <ActionConfirmModal
      ref="deleteConfirmModal"
      :modalId="delConfModalId"
      :confirmTitle="$t('deleteConfirmMsg')"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapMutations } from 'vuex'
import { utils as formUtils } from '@/plugin/InputChange'
import navToContactDetailMixin from './navToContactDetailMixin'

import ActionConfirmModal from '@/controls/ActionConfirmModal'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import mutationTypes from '@/store/mutation-types'
import TextField from '@/controls/TextField'
import formFullnameMixin from '@/pages/formFullnameMixin'

export default {
  name: 'EmergencyContact',
  components: {
    HKLSelect,
    TextField,
    ActionConfirmModal,
  },
  mixins: [navToContactDetailMixin, formFullnameMixin],
  props: {
    tenancyId: [String, Number],
    companyId: [String, Number],
    allowLinkContacts: Boolean,
    linkFunc: Function,
  },
  data () {
    return {
      delConfModalId: `emerCont-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`,
      inputRequireHighlight: false,
      setOrderNumber: true,
      orderNumberContactIdMap: new Map(),
      workingHourPositions: [],
      nonWorkingHourPositions: [],
      deep,
    }
  },
  methods: {
    ...mapMutations('ccd', {
      updateTenancyContactPathField: mutationTypes.TENANCY_CONTACT_UPDATE_PATH_FIELD,
      updatePositionsInStore: mutationTypes.TENANCY_CONTACT_UPDATE_EMERGENCY_POSITION,
    }),
    getKey (key) {
      return key + '_' + Date.now()
    },
    updatePathField (index, fieldName, newValue) {
      this.updateTenancyContactPathField({
        index,
        path: [fieldName],
        newValue,
      })
      formUtils.setDirty()
    },
    updateEmergencyPositions (params) {
      this.updatePositionsInStore(params)
      formUtils.setDirty()
    },
    updateRemarks (index, val) {
      this.updatePathField(index, 'oRemarks', val)
    },
    openDeleteConfirm (indexToUpdate, key) {
      this.$refs.deleteConfirmModal.open(this.removeContact, [indexToUpdate, key])
    },
    removeContact (indexToUpdate, key) {
      if (this.emergencyContacts[key].workingHourOrder) {
        this.updatePositions(key, 'workingHourOrder')
      }
      if (this.emergencyContacts[key].nonWorkingHourOrder) {
        this.updatePositions(key, 'nonWorkingHourOrder')
      }
      this.updatePathField(indexToUpdate, 'isEmergency', false)
    },
    updatePositionCheckBox (indexToUpdate, arrayOrder, checked, isWorkingHour) {
      const fieldName = isWorkingHour ? 'workingHourOrder' : 'nonWorkingHourOrder'
      if (checked) {
        const position = isWorkingHour ? this.workingHourPositions.length + 1 : this.nonWorkingHourPositions.length + 1
        this.updatePathField(indexToUpdate, fieldName, position)
      } else {
        this.updatePositions(arrayOrder, isWorkingHour ? 'workingHourOrder' : 'nonWorkingHourOrder')
      }
    },

    updatePositions (startIndex, fieldName) {
      const positionsToUpdate = lodash.cloneDeep(this.emergencyContacts)
      const posToRemove = positionsToUpdate[startIndex][fieldName]
      positionsToUpdate.forEach(contact => {
        if (+contact[fieldName] > +posToRemove) {
          contact[fieldName] -= 1
        } else if (+contact[fieldName] === +posToRemove) {
          contact[fieldName] = null
        }
      })
      this.updateEmergencyPositions({ positionsToUpdate })
      this.$forceUpdate()
    },
    updatePosition (startIndex, field, value) {
      if (value) {
        const newValue = Number(value)
        const positionsToUpdate = lodash.cloneDeep(this.emergencyContacts)
        const originPosition = Number(positionsToUpdate[startIndex][field])
        if (originPosition < newValue) {
          positionsToUpdate.forEach(item => {
            if (Number(item[field]) > Number(originPosition) && Number(item[field]) <= newValue) {
              item[field] -= 1
            }
          })
        } else if (originPosition > newValue) {
          positionsToUpdate.forEach(item => {
            if (Number(item[field]) >= Number(newValue) && Number(item[field]) < originPosition) {
              item[field] = Number(item[field]) + 1
            }
          })
        }
        positionsToUpdate[startIndex][field] = value
        this.updateEmergencyPositions({ positionsToUpdate })
        this.$forceUpdate()
      }
    },
    getAllPhones (item) {
      return item.allPhones && item.allPhones.length > 0 ? item.allPhones.join(', ') : (item.phone ? item.phone : '--')
    },
    isInvalid () {
      this.inputRequireHighlight = false
      const invalid = this.emergencyContacts.some(item => !item.workingHourOrder && !item.nonWorkingHourOrder)
      this.inputRequireHighlight = invalid
      return invalid
    },
    isItemRequireHighlight (item) {
      return this.inputRequireHighlight && !item.nonWorkingHourOrder && !item.workingHourOrder
    },
  },
  computed: {
    ...mapGetters('ccd', ['currentTenancy']),
    ...mapGetters('user', ['isTenant', 'isEngineer']),
    workingHourPositionHklOptions () {
      return (this.workingHourPositions || []).map((pos) => ({
        name: String(pos),
        value: pos,
      }))
    },
    nonWorkingHourPositionHklOptions () {
      return (this.nonWorkingHourPositions || []).map((pos) => ({
        name: String(pos),
        value: pos,
      }))
    },
    itemHeaders () {
      return [{
        name: this.$t('tenancy.table.name'),
      }, {
        name: this.$t('general.phone'),
      }, {
        name: this.$t('tenancy.table.officeHours'),
        class: 'workinghour-position',
      }, {
        name: this.$t('tenancy.table.position'),
        class: ['workinghour-position', 'column-header-position'],
      }, {
        name: this.$t('tenancy.table.nonOfficeHours'),
        class: 'non-workinghour-position',
      }, {
        name: this.$t('tenancy.table.position'),
        class: ['non-workinghour-position', 'column-header-position'],
      }]
    },
    emergencyContacts () {
      if (!(this.currentTenancy && this.currentTenancy.tenancyContacts)) return []
      this.workingHourPositions = []
      this.nonWorkingHourPositions = []
      let emergencyContacts = this.currentTenancy.tenancyContacts.reduce((partial, item, index) => {
        if (item.CompanyContact.isEmergency) {
          let contact = {
            id: item.id,
            index,
            name: item.name,
            remarks: item.remarks,
            phone: item.phone,
            allPhones: item.allPhones,
            nonWorkingHourOrder: null,
            workingHourOrder: null,
            oRemarks: deep(item, 'CompanyContact.oRemarks'),
            orderNumber: 0,
          }
          if (item.CompanyContact.workingHourOrder) {
            this.workingHourPositions.push(this.workingHourPositions.length + 1)
            contact.workingHourOrder = deep(item, 'CompanyContact.workingHourOrder')
          }
          if (item.CompanyContact.nonWorkingHourOrder) {
            this.nonWorkingHourPositions.push(this.nonWorkingHourPositions.length + 1)
            contact.nonWorkingHourOrder = deep(item, 'CompanyContact.nonWorkingHourOrder')
          }
          partial.push(contact)
        }
        return partial
      }, [])
      if (this.setOrderNumber) {
        emergencyContacts = lodash.orderBy(emergencyContacts, ['workingHourOrder', 'nonWorkingHourOrder'], ['asc', 'asc'])
        emergencyContacts.forEach((item, index) => {
          this.orderNumberContactIdMap.set(item.id, index)
        })
        this.setOrderNumber = false
      }
      emergencyContacts.map(item => {
        item.orderNumber = this.orderNumberContactIdMap.get(item.id)
        return item
      })
      return lodash.orderBy(emergencyContacts, ['orderNumber'], ['asc'])
    },
  },
}
</script>
<style lang="scss" scoped>
@import '~styles/tender/view';
@import '~styles/table';
@import '~styles/components/tenancy-contacts';

.non-workinghour-position {
  background-color: #e2e2e2;
}
.workinghour-position {
  background-color: #ececec;
}
.column-header-position {
  width: 10%;
}

.column-header-remarks {
  background-color: #d8d8d8
}

.error-highlight-background {
  background-color: #f9ecea;
}

</style>
